import type {
  BookingResponse,
  BookingStatus,
  CalendarStock,
  CartResponse,
  CheckoutResponse,
  CustomerInfo,
  InventoriesResponse,
  OptionStock,
  Product,
} from "../types";

/**
 * Client API pour communiquer avec le backend Bonjour.fun
 */
export class ApiClient {
  private apiKey: string;
  private baseUrl: string;
  private debug: boolean;

  constructor(
    apiKey: string,
    baseUrl = "http://localhost:3000",
    debug = false,
  ) {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl.replace(/\/$/, ""); // Retirer le slash final
    this.debug = debug;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  private log(...args: unknown[]) {
    if (this.debug) {
      console.log("[BonjourWidget API]", ...args);
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers = new Headers(options.headers);
    headers.set("X-API-Key", this.apiKey);

    if (options.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    this.log("Request:", options.method || "GET", url);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      const data = await response.json();
      this.log("Response:", data);
      return data as T;
    } catch (error) {
      this.log("Error:", error);
      throw error;
    }
  }

  /**
   * Récupère les informations d'un produit
   */
  async getProduct(productId: string): Promise<Product> {
    return this.request<Product>(`/api/public/products/${productId}`);
  }

  /**
   * Récupère les inventaires pour le calendrier (dates disponibles)
   */
  async getCalendarInventories(
    productId: string,
    params?: {
      startDate?: string;
    },
  ): Promise<CalendarStock[]> {
    const searchParams = new URLSearchParams();
    searchParams.set("type", "calendar");

    if (params?.startDate) searchParams.set("startDate", params.startDate);

    const query = searchParams.toString();
    const endpoint = `/api/public/products/${productId}/inventories?${query}`;

    const response = await this.request<InventoriesResponse>(endpoint);
    return response.inventories as CalendarStock[];
  }

  /**
   * Récupère les inventaires détaillés par option pour une date spécifique
   */
  async getOptionInventories(
    productId: string,
    date: string,
    optionId?: string,
  ): Promise<OptionStock[]> {
    const searchParams = new URLSearchParams();
    searchParams.set("type", "option");
    searchParams.set("date", date);

    if (optionId) searchParams.set("optionId", optionId);

    const query = searchParams.toString();
    const endpoint = `/api/public/products/${productId}/inventories?${query}`;

    const response = await this.request<InventoriesResponse>(endpoint);
    return response.inventories as OptionStock[];
  }

  /**
   * Récupère les inventaires pour les bons cadeaux (vue d'ensemble par option)
   */
  async getGiftInventories(productId: string): Promise<OptionStock[]> {
    const searchParams = new URLSearchParams();
    searchParams.set("type", "gift");

    const query = searchParams.toString();
    const endpoint = `/api/public/products/${productId}/inventories?${query}`;

    const response = await this.request<InventoriesResponse>(endpoint);
    return response.inventories as OptionStock[];
  }

  /**
   * Crée une nouvelle réservation
   */
  async createBooking(data: {
    productId: string;
    optionId: string;
    date: string | null;
    slotId?: string;
    customerInfo: CustomerInfo;
    inventoryId?: string;
  }): Promise<BookingResponse> {
    return this.request<BookingResponse>("/api/public/bookings/create", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ==================== CART API ====================

  /**
   * Ajoute une réservation au panier
   */
  async addToCart(data: {
    productId: string;
    optionId: string;
    date: string | null;
    slotId?: string;
    tickets: Record<string, number>;
    sessionToken?: string;
    additionalFieldsData?: Array<{
      unitId: string;
      unitName: string;
      ticketNumber: number;
      fields: Record<string, string>;
    }>;
  }): Promise<{
    success: boolean;
    cartId: string;
    cartNumber: string;
    bookingId: string;
    itemsTotal: number;
    merged: boolean;
    bookingItems: Array<{
      id: string;
      unitId: string | null;
      unitName: string;
      quantity: number;
      price: number;
      promotionPrice: number | null;
    }>;
    createdAt: string;
    sessionToken: string;
  }> {
    return this.request("/api/public/cart/add", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * Récupère le contenu d'un panier par ID
   */
  async getCart(cartId: string): Promise<CartResponse> {
    return this.request<CartResponse>(`/api/public/cart/${cartId}`);
  }

  /**
   * Récupère le panier actif d'une session
   */
  async getCartBySession(sessionToken: string): Promise<CartResponse> {
    return this.request<CartResponse>(
      `/api/public/cart/by-session/${sessionToken}`,
    );
  }

  /**
   * Supprime un item du panier
   */
  async removeFromCart(
    cartId: string,
    bookingId: string,
  ): Promise<{ success: boolean }> {
    return this.request(`/api/public/cart/${cartId}?bookingId=${bookingId}`, {
      method: "DELETE",
    });
  }

  /**
   * Vide entièrement le panier (supprime tous les bookings et libère l'inventaire)
   */
  async clearCart(cartId: string): Promise<{ success: boolean }> {
    return this.request(`/api/public/cart/${cartId}/clear`, {
      method: "POST",
    });
  }

  /**
   * Checkout du panier - génère le Payment Intent
   */
  async checkoutCart(
    cartId: string,
    customerInfo: CustomerInfo,
  ): Promise<CheckoutResponse> {
    return this.request<CheckoutResponse>(
      `/api/public/cart/${cartId}/checkout`,
      {
        method: "POST",
        body: JSON.stringify({ customerInfo }),
      },
    );
  }

  /**
   * Récupère le statut d'une réservation
   */
  async getBookingStatus(bookingId: string): Promise<BookingStatus> {
    return this.request<BookingStatus>(
      `/api/public/bookings/${bookingId}/status`,
    );
  }

  /**
   * Polling du statut de réservation jusqu'à confirmation
   */
  async pollBookingStatus(
    bookingId: string,
    options: {
      maxAttempts?: number;
      intervalMs?: number;
      onUpdate?: (status: BookingStatus) => void;
    } = {},
  ): Promise<BookingStatus> {
    const { maxAttempts = 30, intervalMs = 2000, onUpdate } = options;

    let attempts = 0;

    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        attempts++;

        try {
          const status = await this.getBookingStatus(bookingId);

          if (onUpdate) {
            onUpdate(status);
          }

          // Vérifier si le paiement est confirmé
          if (
            status.status === "CONFIRMED" &&
            status.payment?.state === "COMPLETED"
          ) {
            clearInterval(interval);
            resolve(status);
          } else if (attempts >= maxAttempts) {
            clearInterval(interval);
            reject(new Error("Polling timeout: booking status not confirmed"));
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, intervalMs);
    });
  }
}
